/**
 * DASHBOARD LAYOUT - CHATBOT IMÓVEIS®
 * =================================================================
 * Layout wrapper para todas as páginas do dashboard
 *
 * Integra: Topbar + Sidebar + Footer + CommandMenu (futuro)
 *
 * Data: 2025-11-09
 */

import React, { useState } from 'react';
import { Topbar, Sidebar, Footer, useSidebarKeyboard } from '@/components/nav';
import { CommandMenu } from '@/components/search/CommandMenu';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

/**
 * Layout completo do dashboard com navegação integrada
 *
 * Uso em App.tsx:
 * ```tsx
 * <Route path="/dashboard/*">
 *   <DashboardLayout>
 *     <Switch>
 *       <Route path="/dashboard" component={DashboardCentral} />
 *       <Route path="/dashboard/profile" component={ProfilePage} />
 *       ...
 *     </Switch>
 *   </DashboardLayout>
 * </Route>
 * ```
 */
export function DashboardLayout({ children }: DashboardLayoutProps) {
  // Estado de sidebar colapsada
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Estado de CommandMenu (Fase 10 - futuro)
  const [commandMenuOpen, setCommandMenuOpen] = useState(false);

  // Contadores de badges (TODO: integrar com dados reais)
  const [badges] = useState({
    leads: 0, // TODO: integrar com LeadsContext
    whatsapp: 0, // TODO: integrar com WhatsApp unread
    notifications: 0, // TODO: integrar com NotificationsContext
  });

  // Atalho de teclado Ctrl+B para toggle sidebar
  useSidebarKeyboard(() => setSidebarCollapsed(prev => !prev));

  return (
    <div
      className='min-h-screen flex flex-col bg-gray-50 dark:bg-gray-950'
      data-testid='dashboard-root'
    >
      {/* ========================================
       * TOPBAR - Fixed no topo
       * ======================================== */}
      <Topbar
        onSearchClick={() => setCommandMenuOpen(true)}
        notificationCount={badges.notifications}
        sidebarContent={
          <Sidebar
            badges={badges}
            collapsed={false} // Sempre expandida no mobile sheet
          />
        }
      />

      {/* ========================================
       * LAYOUT - Sidebar + Main Content
       * ======================================== */}
      <div className='flex flex-1'>
        {/* Desktop Sidebar */}
        <div className='hidden lg:block'>
          <Sidebar
            collapsed={sidebarCollapsed}
            onCollapseChange={setSidebarCollapsed}
            badges={badges}
          />
        </div>

        {/* Main Content */}
        <main
          className={`flex-1 pt-[64px] transition-all duration-brand-normal ease-brand
                     ${sidebarCollapsed ? 'lg:pl-[72px]' : 'lg:pl-[240px]'}`}
        >
          {children}
        </main>
      </div>

      {/* ========================================
       * FOOTER
       * ======================================== */}
      <Footer />

      {/* ========================================
       * COMMAND MENU - Busca Global (CMD/CTRL + K)
       * ======================================== */}
      <CommandMenu open={commandMenuOpen} onOpenChange={setCommandMenuOpen} />
    </div>
  );
}
